import { createContext, useContext, useRef, useState } from 'react';

import { Trans } from '@lingui/react/macro';
import classNames from 'classnames';
import { useAtomValue } from 'jotai';

import { ExternalPatientContext } from '@/app/external/constants';
import { ZoomToolTopbar } from '@/common-components/elements/ZoomToolTopbar.tsx/ZoomToolTopbar';
import { previewDisplayDeviceAtom } from '@/modules/annotator/multiFileViewer/imageToolbar/atoms';
import type { TVisitSummary } from '@/react-query/useVisitSummaries';

import { usePatientPrintoutData } from '../hooks/usePatientPrintoutData';
import { PatientPrintout } from '../PatientPrintout';

import { PreviewSidebar } from './PreviewSidebar';

export const ThemeModeContext = createContext<'light' | 'dark'>('dark');

// Mobile device frame component - moved outside to prevent recreation on every render
const MobileDeviceFrame = ({ children }: { children: React.ReactNode }) => {
  const { isIframe } = useContext(ExternalPatientContext);
  const mode = useContext(ThemeModeContext);

  return (
    <div
      className={classNames(
        'vai-flex vai-justify-center vai-w-full vai-h-full',
        {
          'vai-bg-background-dark': mode === 'dark',
          'vai-bg-slate-50': mode === 'light',
          'vai-items-center': !isIframe,
        },
      )}
    >
      <div className="vai-relative vai-h-[90%] vai-max-w-[400px] vai-max-h-[850px] vai-min-w-[360px] vai-min-h-[600px] vai-aspect-[375/812] vai-bg-black vai-rounded-[40px] vai-p-3 vai-m-6 vai-shadow-2xl">
        {/* Notch */}
        <div className="vai-absolute vai-top-0 vai-left-1/2 vai-transform vai--translate-x-1/2 vai-w-[126px] vai-h-[30px] vai-bg-black vai-rounded-b-[18px] vai-z-10" />
        {/* Screen */}
        <div className="vai-w-full vai-h-full vai-bg-white vai-rounded-[32px] vai-overflow-hidden">
          {children}
        </div>
      </div>
    </div>
  );
};

interface Props {
  isLoading: boolean;
  mode?: 'light' | 'dark';
  visitSummaries?: TVisitSummary[];
}

export const QRPrintDownloadContent = ({
  isLoading,
  mode = 'dark',
  visitSummaries,
}: Props) => {
  const [scale, setScale] = useState(1);
  const [hasInitiallyScaled, setHasInitiallyScaled] = useState(false);
  const previewDisplayDevice = useAtomValue(previewDisplayDeviceAtom);

  const componentRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const { visitIdToImagesMap } = usePatientPrintoutData();

  const isMobile = previewDisplayDevice === 'mobile';

  return (
    <ThemeModeContext.Provider value={mode}>
      <div
        className="vai-flex vai-flex-col vai-h-full"
        data-testid="preview-dialog"
      >
        {/* Top Bar */}
        <div
          className={classNames('vai-px-9 vai-py-4 vai-border-b', {
            'vai-border-b-zinc-800': mode === 'dark',
            'vai-border-b-zinc-200': mode === 'light',
          })}
        >
          <p
            className={classNames(
              'vai-font-inter vai-text-2xl vai-font-semibold',
              {
                'vai-text-zinc-50': mode === 'dark',
                'vai-text-zinc-900': mode === 'light',
              },
            )}
          >
            <Trans>Patient Education Summary</Trans>
          </p>
        </div>

        <div className="vai-flex vai-overflow-auto vai-flex-1">
          <div
            className={classNames(
              'vai-flex vai-flex-col vai-p-4 vai-items-center vai-w-[72%]',
              { 'vai-bg-slate-50': mode === 'light' },
            )}
          >
            <ZoomToolTopbar
              contentRef={contentRef}
              componentRef={componentRef}
              scale={scale}
              hasInitiallyScaled={hasInitiallyScaled}
              onSetHasInitiallyScaled={setHasInitiallyScaled}
              onSetScale={setScale}
              isDarkMode={mode === 'dark'}
            />

            {/* Main Content Area */}
            <div
              className={classNames('vai-flex-1 vai-w-full vai-overflow-auto', {
                'vai-bg-background-dark': mode === 'dark' && isMobile,
                'vai-bg-white vai-pt-8': !isMobile,
                'vai-border vai-border-zinc-200 vai-rounded-md':
                  mode === 'light' && !isMobile,
              })}
              ref={contentRef}
            >
              {isLoading && (
                <div className="vai-flex vai-items-center vai-justify-center vai-h-full vai-w-full">
                  <div className="vai-animate-spin vai-rounded-full vai-h-6 vai-w-6 vai-border-2 vai-border-sapphire-600 vai-border-t-transparent" />
                </div>
              )}
              {!isLoading && visitSummaries?.length && (
                <>
                  {isMobile ? (
                    <MobileDeviceFrame
                      key={visitSummaries
                        .map((visit) => visit.estimatedVisitId)
                        .join('-')}
                    >
                      <div
                        className="vai-w-full vai-h-full vai-overflow-auto"
                        ref={componentRef}
                      >
                        <PatientPrintout
                          isLoading={isLoading}
                          scale={scale}
                          visitSummaries={visitSummaries}
                          visitIdToImagesMap={visitIdToImagesMap}
                        />
                      </div>
                    </MobileDeviceFrame>
                  ) : (
                    <div ref={componentRef}>
                      <PatientPrintout
                        isLoading={isLoading}
                        scale={scale}
                        visitSummaries={visitSummaries}
                        visitIdToImagesMap={visitIdToImagesMap}
                      />
                    </div>
                  )}
                </>
              )}

              {!visitSummaries?.length && !isLoading && (
                <div className="vai-flex vai-items-center vai-justify-center vai-h-full vai-w-full vai-text-gray-500">
                  <div className="vai-text-center">
                    <Trans>
                      No image selected. Please select an image to preview the
                      patient education summary.
                    </Trans>
                  </div>
                </div>
              )}
            </div>
          </div>

          <PreviewSidebar componentRef={componentRef} />
        </div>
      </div>
    </ThemeModeContext.Provider>
  );
};
